import { css } from '@styled-system/css';
import { type FC } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { Message } from '@/shared/components/ui/Message';
import { ProductItemData } from '@/shared/types/product';

import { ProductItem } from './ProductItem';
import { ProductItemSkeleton } from './ProductItemSkeleton';

interface ProductsGridProps {
  products: ProductItemData[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
}

export const ProductsGrid: FC<ProductsGridProps> = ({
  products,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
}) => {
  const contentContainerStyles = css({
    flex: 1,
  });

  const productsGridStyles = css({
    display: 'grid',
    overflow: 'hidden', // Le overflow est géré plus haut dans la hiérarchie
    gridTemplateColumns: {
      base: 'minmax(0, 1fr)',
      lg: 'repeat(2, minmax(0, 1fr))',
    },
    gap: '3.125rem',
    py: '0.5rem',
    px: 0,
    w: '100%',
    alignItems: 'start',
    justifyItems: 'start',
  });

  const loaderContainerStyles = css({
    display: 'grid',
    gridTemplateColumns: {
      base: 'minmax(0, 1fr)',
      lg: 'repeat(2, minmax(0, 1fr))',
    },
    gap: '3.125rem',
    py: '0.5rem',
    px: 0,
    alignItems: 'start',
    justifyItems: 'start',
  });

  const emptyStateStyles = css({
    display: 'flex',
    w: '100%',
    h: '100%',
    mt: '1rem',
  });

  if (isLoading && products.length === 0) {
    // Loading initial des produits
    return (
      <div className={contentContainerStyles}>
        <div className={loaderContainerStyles}>
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    // État vide
    return (
      <div className={contentContainerStyles}>
        <Message type="info" bordered className={emptyStateStyles}>
          Aucun produit trouvé pour les filtres sélectionnés
        </Message>
      </div>
    );
  }

  // Grille de produits avec infinite scroll
  return (
    <div className={contentContainerStyles}>
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        hasMore={hasNextPage}
        loader={
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <ProductItemSkeleton key={`infinite-loader-${i}`} />
            ))}
          </>
        }
        useWindow={false}
        element="div"
        className={productsGridStyles}
      >
        {products.map((product) => (
          <ProductItem key={product.id} product={product} />
        ))}
      </InfiniteScroll>
    </div>
  );
};
